import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'

import { usePromptImageAttachments } from '@/composables/usePromptImageAttachments'
import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import { screenIndex } from '@/lib/setup/setup-screen'
import {
  VISUAL_STYLE_NAVIGATOR,
  type VisualStyleMove,
  resumeVisualStyleScreen,
} from '@/lib/setup/visual-style-navigation'
import { CONFIRM_STAMP_WAIT_MS, whenChanged } from '@/lib/setup/when-changed'
import { ensureFontFaces } from '@/lib/templates/font-faces'
import {
  type SlideFill,
  type TemplateDoc,
  type TemplateLibrary,
  loadTemplateLibrary,
} from '@/lib/templates/template-loader'
import {
  type TemplateColourMode,
  type ThemeChoice,
  brandThemeOf,
  shippedFontOf,
} from '@/lib/templates/template-theme'
import { confirmVisualStyle as repoConfirmVisualStyle, readBrandStyle } from '@/repositories/design'
import { storageTemplateFiles } from '@/repositories/templates'
import { useBrandStore } from '@/stores/brand'
import { useSetupPositionStore } from '@/stores/setup-position'
import { currentSetupScope } from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import type { Activity } from '@/types/activity'
import {
  type BrandStyle,
  type ConfirmVisualStyleReason,
  DEFAULT_BRAND_STYLE_TEMPLATE_ID,
  OWN_DESIGNS_MAX_ITEMS,
  type VisualStyleChoice,
} from '@/types/design'
import { type LoadState, failed, isError, isLoaded, loaded, loading } from '@/types/load-state'
import type { StepConfirmation } from '@/types/onboarding-chain'

/** Where the template library stands. */
export type TemplateLibraryState = 'idle' | 'loading' | 'ready' | 'failed'

type TemplatePick = Omit<BrandStyle, 'ownDesigns'>

const DEFAULT_PICK: TemplatePick = {
  templateId: DEFAULT_BRAND_STYLE_TEMPLATE_ID,
  accent: null,
  font: 'template',
}

const REFUSAL_MESSAGE_KEYS: Record<ConfirmVisualStyleReason, string> = {
  visual_style_strategy_missing: 'setup.visualStyle.errors.strategyMissing',
  visual_style_designs_missing: 'setup.visualStyle.errors.designsMissing',
  owner_admin_role_required: 'setup.visualStyle.errors.roleRequired',
}

function isRefusalReason(reason: string | undefined): reason is ConfirmVisualStyleReason {
  return reason !== undefined && reason in REFUSAL_MESSAGE_KEYS
}

/** Step five of setup: the saved brand style, the pick on the screens, the template library and the confirm. */
export const useVisualStyleSetupStore = defineStore('setup-visual-style', () => {
  const brand = useBrandStore()
  const ownUploads = usePromptImageAttachments({ max: OWN_DESIGNS_MAX_ITEMS })

  const brandStyle = ref<LoadState<BrandStyle | null>>(loading())
  const templateId = ref(DEFAULT_PICK.templateId)
  const accent = ref(DEFAULT_PICK.accent)
  const font = ref(DEFAULT_PICK.font)
  const colourMode = ref<TemplateColourMode>('brand')
  const confirming = ref(false)
  const library = shallowRef<TemplateLibrary | null>(null)
  const libraryState = ref<TemplateLibraryState>('idle')
  let readToken = 0

  function seed(pick: TemplatePick): void {
    templateId.value = pick.templateId
    accent.value = pick.accent
    font.value = pick.font
  }

  function open(): void {
    const scope = currentSetupScope()
    const token = ++readToken
    if (!scope) {
      brandStyle.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }
    brandStyle.value = loading()
    readBrandStyle(scope)
      .then((style) => {
        if (token !== readToken) return
        brandStyle.value = loaded(style)
        seed(style ?? DEFAULT_PICK)
      })
      .catch((cause: unknown) => {
        if (token === readToken) brandStyle.value = failed(parseCallableError(cause).message)
      })
  }

  function close(): void {
    readToken += 1
    brandStyle.value = loading()
    seed(DEFAULT_PICK)
    colourMode.value = 'brand'
    ownUploads.reset()
  }

  const resolved = computed(() => brandStyle.value.status !== 'loading')
  const readFailed = computed(() => isError(brandStyle.value))
  const activity = computed<Activity>(() => 'idle')

  const confirmedAt = computed(() =>
    isLoaded(brand.brand) ? brand.brand.value.visualStyleConfirmedAt : null,
  )

  const confirmation = computed<StepConfirmation>(() => ({
    confirmed: confirmedAt.value !== null,
    confirmedAt: confirmedAt.value,
  }))

  /** The library item ids of the saved own designs. */
  const savedOwnDesignIds = computed(() =>
    isLoaded(brandStyle.value) ? (brandStyle.value.value?.ownDesigns?.libraryItemIds ?? []) : [],
  )

  function resumeIndex(): number {
    return screenIndex(
      VISUAL_STYLE_NAVIGATOR.screens,
      resumeVisualStyleScreen({
        confirmed: confirmation.value.confirmed,
        ownDesigns: savedOwnDesignIds.value.length > 0,
      }),
    )
  }

  function move(applied: VisualStyleMove): void {
    useSetupPositionStore().move('visual-style', VISUAL_STYLE_NAVIGATOR, applied)
  }

  /** Picks a template and opens it. */
  function openTemplate(id: string): void {
    templateId.value = id
    move('open-template')
  }

  const visualStyle = computed(() => (isLoaded(brand.visualStyle) ? brand.visualStyle.value : null))
  const colors = computed<readonly string[]>(() => visualStyle.value?.colors ?? [])
  const templates = computed(() => library.value?.templates ?? [])

  /** The brand font when the templates ship it, else `null`. */
  const brandFont = computed(() =>
    shippedFontOf(visualStyle.value?.fontFamily ?? null, library.value?.fonts ?? []),
  )

  watch(brandFont, (family) => {
    if (family !== null && library.value) void ensureFontFaces(library.value.fontFaces, family)
  })

  const selectedTemplate = computed(
    () => templates.value.find((doc) => doc.id === templateId.value) ?? null,
  )

  /** The brand dress a pick puts on a template; the slides show the monogram, else the logo. */
  function fillFor(doc: TemplateDoc, choice: ThemeChoice): SlideFill {
    return {
      theme: brandThemeOf(colors.value, brandFont.value, choice, doc.theme),
      brandName: isLoaded(brand.brand) ? brand.brand.value.name : '',
      logo: visualStyle.value?.monogram?.src ?? visualStyle.value?.logo?.src,
    }
  }

  /** Every template's title dress in the brand colours, by template id. */
  const brandFills = computed<Readonly<Record<string, SlideFill>>>(() =>
    Object.fromEntries(
      templates.value.map((doc) => [doc.id, fillFor(doc, { accent: null, font: 'template' })]),
    ),
  )

  /** The template on show, dressed with the picked accent and font. */
  const templateFill = computed(() =>
    selectedTemplate.value
      ? fillFor(selectedTemplate.value, { accent: accent.value, font: font.value })
      : undefined,
  )

  /** Loads the 17 templates and their fonts once. */
  async function loadLibrary(): Promise<void> {
    if (libraryState.value === 'loading' || libraryState.value === 'ready') return
    libraryState.value = 'loading'
    try {
      library.value = await loadTemplateLibrary(storageTemplateFiles())
      libraryState.value = 'ready'
    } catch {
      libraryState.value = 'failed'
    }
  }

  async function confirm(choice: VisualStyleChoice, saved: BrandStyle): Promise<boolean> {
    confirming.value = true
    try {
      const before = confirmedAt.value
      await repoConfirmVisualStyle({ choice })
      brandStyle.value = loaded(saved)
      await whenChanged(() => confirmedAt.value, before, CONFIRM_STAMP_WAIT_MS)
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      const { t } = i18n.global
      useToastStore().error(
        t('setup.errors.confirmFailed'),
        isRefusalReason(parsed.reason) ? t(REFUSAL_MESSAGE_KEYS[parsed.reason]) : parsed.message,
      )
      return false
    } finally {
      confirming.value = false
    }
  }

  /** Confirms the template on show with the picked accent and font. */
  function confirmTemplate(): Promise<boolean> {
    const pick = { templateId: templateId.value, accent: accent.value, font: font.value }
    return confirm({ kind: 'template', ...pick }, { ...pick, ownDesigns: null })
  }

  /** Waits for the uploads to land, then confirms them as the brand's own designs. */
  async function confirmOwnDesigns(): Promise<boolean> {
    const libraryItemIds = (await ownUploads.settled()).map(({ itemId }) => itemId)
    if (libraryItemIds.length === 0) return false
    return confirm(
      { kind: 'own-designs', libraryItemIds },
      { ...DEFAULT_PICK, ownDesigns: { libraryItemIds } },
    )
  }

  return {
    brandStyle,
    savedOwnDesignIds,
    templateId,
    accent,
    font,
    colourMode,
    confirming,
    templates,
    libraryState,
    colors,
    brandFont,
    selectedTemplate,
    brandFills,
    templateFill,
    ownImages: ownUploads.images,
    ownUploading: ownUploads.isUploadingAny,
    addOwnDesigns: ownUploads.onAttach,
    resolved,
    readFailed,
    confirmation,
    activity,
    open,
    close,
    resumeIndex,
    move,
    openTemplate,
    loadLibrary,
    confirmTemplate,
    confirmOwnDesigns,
  }
})
